import React from 'react';
import {getSetting} from "./utils/settings";
import {useChatContext} from "./ChatContext";
import {sendMessage} from "./api/requests";

const PrimaryAction: React.FC = () => {
    const primaryActionCallback   = getSetting('shortcuts.primary_action_callback');
    const primaryActionLabel      = getSetting('shortcuts.primary_action_label');
    const primaryActionMessage    = getSetting('shortcuts.primary_action_message');

    const {chat, history} = useChatContext();

    const callback = (): void => {
        chat.open();

        setTimeout(() => {
            history.addMessage({
                sender: "bot",
                text: primaryActionMessage,
                type: primaryActionCallback,
                timestamp: Date.now()
            });
        }, 500);
    }

    return (
        <button id="wvcb__primary-call-to-action" onClick={callback}>{primaryActionLabel}</button>
    );
}

const SecondaryAction: React.FC = () => {
    const secondaryActionCallback   = getSetting('shortcuts.secondary_action_callback');
    const secondaryActionLabel      = getSetting('shortcuts.secondary_action_label');
    const secondaryActionMessage    = getSetting('shortcuts.secondary_action_message');
    const contactPerson             = getSetting('contact_person');

    if ( !contactPerson ) {
        return null;
    }
    const {chat, history} = useChatContext();

    const callback = (): void => {
        chat.open();

        setTimeout(() => {
            history.addMessage({
                sender: "bot",
                text: secondaryActionMessage,
                type: secondaryActionCallback,
                timestamp: Date.now(),
                data: contactPerson.id
            });
        }, 500);
    }

    return (
        <button id="wvcb__secondary-call-to-action" onClick={callback}>
            <img  id="wvcb__secondary-call-to-action-image" src={contactPerson.image} alt={contactPerson.firstname + ' ' + contactPerson.lastname}/>
            <span id="wvcb__secondary-call-to-action-name">{contactPerson.firstname} {contactPerson.lastname}</span>
            <span id="wvcb__secondary-call-to-action-label">{secondaryActionLabel}</span>
        </button>
    );
}

export function Shortcuts(): React.ReactElement {
    const { chat, history, input } = useChatContext();

    /**
     * Change Input
     *
     * @param {any} event
     * @return {void}
     */
        //@ts-ignore
    const changeInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
        input.setInput(event.target.value)
    };

    /**
     * Focus Input
     *
     * @return {void}
     */
    const focusInput = (): void => {
        document.getElementById("wvcb__input-field")?.focus()
    };

    /**
     * Submit Input
     *
     * @param {React.KeyboardEvent<HTMLInputElement>} event
     * @return {Promise<void>}
     */
    const submitInput = async (event: React.KeyboardEvent<HTMLInputElement>): Promise<void> => {
        if (event.key === 'Enter' && !event.shiftKey && !chat.isLoading()) {
            event.preventDefault();
            await submitMessage();
        }
    }

    const submitMessage = async (): Promise<void> => {
        const isLoading = chat.isLoading();
        const message   = input.getInput();

        if (isLoading || message === '') {
            return;
        }

        input.clearInput();

        history.addMessage({
            sender: 'user',
            text: message,
            type: 'request',
        });

        // Check if the message limit has been reached.
        if ( history.reachedLimit() ) {
            history.addMessage({
                sender: 'bot',
                text: getSetting( 'error_messages.message_limit' ),
                type: 'error',
            });

            return;
        }

        // Check if the message matches any of the predefined commands for forms.
        else {
            chat.load();

            switch ( true ) {
                // Appointment-Form Message
                case message === getSetting( 'appointment_form.command' ):
                    history.addMessage({
                        sender: 'bot',
                        text: 'Terminbuchungsformular:',
                        type: 'appointment-form'
                    });
                    break;
                // Callback-Form Message
                case message === getSetting( 'callback_form.command' ):
                    history.addMessage({
                        sender: 'bot',
                        text: 'Rückrufformular:',
                        type: 'callback-form'
                    });
                    break;
                // Contact-Form Message
                case message === getSetting( 'contact_form.command' ):
                    history.addMessage({
                        sender: 'bot',
                        text: 'Kontaktformular:',
                        type: 'contact-form'
                    });
                    break;
                // Default Message
                default:
                    try {
                        const responseMessage = await sendMessage( message );
                        history.addMessage(responseMessage);
                    }

                    // If any error occurs during the fetch or processing, we catch it and display an error message in the chat
                    catch (error) {
                        history.addMessage({
                            sender: 'bot',
                            text: 'Konnte keine Antwort generieren. Bitte versuche es später erneut.',
                        });
                    }
                    break;
            }

            chat.loaded();
        }

    };

    return (
        <div id="wvcb__shortcuts">

            <button id="wvcb__primary-button"
                    aria-label={"Start Chat"}
                    onClick={focusInput}
            ></button>

            <div id="wvcb__left-column">
                <input id="wvcb__input-field"
                       type="text"
                       onChange={changeInput}
                       onFocus={chat.open}
                       onKeyDown={submitInput}
                       placeholder={getSetting('chat.input_placeholder')}
                       value={input.state}
                />
                <button id="wvcb__submit"
                        onClick={submitMessage}
                        disabled={!input.hasInput() || chat.isLoading()}
                ></button>
            </div>

            <div id="wvcb__right-column">
                <div id="wvcb__call-to-actions">
                    <PrimaryAction/>
                    <SecondaryAction/>
                </div>
            </div>

            <div id="wvcb__background"></div>

        </div>
    );
};
